import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { ChevronLeft, ChevronRight, Plus, CalendarX, Loader2, X, ExternalLink } from 'lucide-react';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, format,
  addMonths, subMonths, addWeeks, subWeeks, addDays, subDays, isToday, isSameMonth, parseISO,
} from 'date-fns';
import { calendarApi, projectsApi } from '../api';
import { EventFormDialog } from '../components/EventFormDialog';
import {
  ITEM_COLORS, ITEM_TYPE_LABEL, LEGEND_MANUAL, LEGEND_AUTO, SOURCE_OPTIONS,
  EVENT_TYPE_OPTIONS, eventStatusLabel, fmtTime,
} from '../calendarConstants';

const iso = (d) => format(d, 'yyyy-MM-dd');
const selectCls = 'bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const VIEWS = [['month', 'Month'], ['week', 'Week'], ['day', 'Day']];

const itemOnDay = (it, dayStr) => {
  const s = it.start_date; const e = it.end_date || it.start_date;
  return s && s <= dayStr && dayStr <= e;
};

const ItemPill = ({ it, onClick }) => (
  <button onClick={onClick} className="w-full flex items-center gap-1 text-left text-[11px] rounded px-1.5 py-0.5 truncate hover:opacity-90"
    style={{ backgroundColor: `${ITEM_COLORS[it.item_type]}22`, color: ITEM_COLORS[it.item_type] }}
    data-testid={`cal-item-${it.calendar_item_id}`}>
    <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: ITEM_COLORS[it.item_type] }} />
    <span className="truncate">{!it.is_all_day && it.start_time ? `${fmtTime(it.start_time)} ` : ''}{it.title}</span>
  </button>
);

export const OpsCalendar = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState('month');
  const [current, setCurrent] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(iso(new Date()));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [projects, setProjects] = useState([]);

  const [source, setSource] = useState('all');
  const [projectFilter, setProjectFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createDate, setCreateDate] = useState(null);
  const [createType, setCreateType] = useState('appointment');
  const [editEvent, setEditEvent] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [dayModal, setDayModal] = useState(null);

  const range = useMemo(() => {
    if (view === 'month') return { start: startOfWeek(startOfMonth(current)), end: endOfWeek(endOfMonth(current)) };
    if (view === 'week') return { start: startOfWeek(current), end: endOfWeek(current) };
    return { start: current, end: current };
  }, [view, current]);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const res = await calendarApi.feed({ start_date: iso(range.start), end_date: iso(range.end) });
      setItems(res.data);
    } catch { setError(true); } finally { setLoading(false); }
  }, [range.start, range.end]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { projectsApi.list({ archived: false, sort: 'name_az' }).then((r) => setProjects(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (searchParams.get('new') === 'appointment') {
      setCreateDate(iso(new Date())); setCreateType('appointment'); setCreateOpen(true);
      searchParams.delete('new'); setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => items.filter((it) => {
    if (source !== 'all' && it.source_type !== source) return false;
    if (typeFilter && it.item_type !== typeFilter) return false;
    if (projectFilter && (it.project?.id !== projectFilter)) return false;
    return true;
  }), [items, source, typeFilter, projectFilter]);

  const hasFilters = source !== 'all' || typeFilter || projectFilter;
  const clearFilters = () => { setSource('all'); setTypeFilter(''); setProjectFilter(''); };

  const goPrev = () => setCurrent((d) => (view === 'month' ? subMonths(d, 1) : view === 'week' ? subWeeks(d, 1) : subDays(d, 1)));
  const goNext = () => setCurrent((d) => (view === 'month' ? addMonths(d, 1) : view === 'week' ? addWeeks(d, 1) : addDays(d, 1)));
  const goToday = () => { setCurrent(new Date()); setSelectedDay(iso(new Date())); };

  const heading = view === 'month' ? format(current, 'MMMM yyyy')
    : view === 'week' ? `${format(startOfWeek(current), 'MMM d')} – ${format(endOfWeek(current), 'MMM d, yyyy')}`
    : format(current, 'EEEE, MMMM d, yyyy');

  const dayItems = (dayStr) => filtered.filter((it) => itemOnDay(it, dayStr))
    .sort((a, b) => (a.is_all_day === b.is_all_day ? (a.start_time || '') > (b.start_time || '') ? 1 : -1 : a.is_all_day ? 1 : -1));

  const openItem = async (it) => {
    if (it.source_type === 'manual_event') {
      try { const res = await calendarApi.getEvent(it.source_id); setEditEvent(res.data); setEditOpen(true); }
      catch { setDetailItem(it); }
    } else {
      setDetailItem(it);
    }
  };

  const openCreateForDate = (dayStr) => { setCreateDate(dayStr); setCreateType('appointment'); setCreateOpen(true); };

  const monthDays = eachDayOfInterval({ start: range.start, end: range.end });
  const weekDays = eachDayOfInterval({ start: startOfWeek(current), end: endOfWeek(current) });

  return (
    <div data-testid="ops-calendar">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-white">Calendar</h2>
          <p className="text-gray-400 mt-1">Appointments, quotes, and project schedules in one place.</p>
        </div>
        <Button onClick={() => { setCreateDate(iso(current)); setCreateType('appointment'); setCreateOpen(true); }} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold w-full md:w-auto" data-testid="cal-new-event">
          <Plus size={18} /> New Event
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goPrev} className="border-gray-700 text-gray-300" data-testid="cal-prev" aria-label="Previous"><ChevronLeft size={18} /></Button>
          <Button variant="outline" size="sm" onClick={goToday} className="border-gray-700 text-gray-300" data-testid="cal-today">Today</Button>
          <Button variant="outline" size="icon" onClick={goNext} className="border-gray-700 text-gray-300" data-testid="cal-next" aria-label="Next"><ChevronRight size={18} /></Button>
          <span className="ml-2 text-lg font-semibold text-white" data-testid="cal-heading">{heading}</span>
        </div>
        <div className="flex bg-[#111111] border border-gray-800 rounded-lg p-1" data-testid="cal-views">
          {VIEWS.map(([v, label]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
              className={`px-4 py-1.5 rounded-md text-sm font-medium ${view === v ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'}`}>{label}</button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={source} onChange={(e) => setSource(e.target.value)} className={selectCls} data-testid="cal-filter-source">
          {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={selectCls} data-testid="cal-filter-type">
          <option value="">All Types</option>
          <optgroup label="Manual">
            {EVENT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </optgroup>
          <optgroup label="Automatic">
            <option value="project_start">Project Start</option>
            <option value="project_target">Project Deadline</option>
          </optgroup>
        </select>
        <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className={selectCls} data-testid="cal-filter-project">
          <option value="">All Projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-400 hover:text-white" data-testid="cal-clear-filters"><X size={14} /> Clear Filters</Button>}
      </div>

      {/* Body */}
      {loading ? (
        <div className="text-center py-16 text-gray-500" data-testid="cal-loading"><Loader2 size={26} className="mx-auto mb-2 animate-spin text-[#D4AF37]" />Loading calendar…</div>
      ) : error ? (
        <Card className="bg-[#111111] border-gray-800 p-8 text-center" data-testid="cal-error"><p className="text-red-400 mb-3">Could not load the calendar.</p><Button onClick={load} variant="outline" className="border-gray-700 text-gray-300">Retry</Button></Card>
      ) : view === 'month' ? (
        <>
          <Card className="bg-[#111111] border-gray-800 p-3 mb-4 overflow-hidden">
            <div className="grid grid-cols-7 mb-1">
              {WEEKDAYS.map((d) => <div key={d} className="text-center text-xs font-semibold text-gray-500 py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1" data-testid="cal-month">
              {monthDays.map((day) => {
                const ds = iso(day);
                const its = dayItems(ds);
                return (
                  <div key={ds} onClick={() => setSelectedDay(ds)}
                    className={`min-h-[72px] md:min-h-[104px] rounded-md border p-1 cursor-pointer ${isSameMonth(day, current) ? 'border-gray-800 bg-[#161616]' : 'border-transparent bg-[#0E0E0E]'} ${selectedDay === ds ? 'ring-1 ring-[#D4AF37]' : ''}`}
                    data-testid={`cal-day-${ds}`}>
                    <div className={`text-xs font-medium h-6 w-6 flex items-center justify-center rounded-full ${isToday(day) ? 'bg-[#D4AF37] text-black' : isSameMonth(day, current) ? 'text-gray-300' : 'text-gray-600'}`}>{format(day, 'd')}</div>
                    <div className="mt-1 space-y-1 hidden md:block">
                      {its.slice(0, 2).map((it) => <ItemPill key={it.calendar_item_id} it={it} onClick={(e) => { e.stopPropagation(); openItem(it); }} />)}
                      {its.length > 2 && <button onClick={(e) => { e.stopPropagation(); setDayModal(ds); }} className="text-[10px] text-gray-400 hover:text-[#D4AF37]" data-testid={`cal-more-${ds}`}>+ {its.length - 2} more</button>}
                    </div>
                    {its.length > 0 && <div className="md:hidden mt-1 flex gap-0.5 flex-wrap">{its.slice(0, 4).map((it) => <span key={it.calendar_item_id} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: ITEM_COLORS[it.item_type] }} />)}</div>}
                  </div>
                );
              })}
            </div>
          </Card>
          {/* Selected-day agenda (mobile + quick view) */}
          <AgendaCard title={format(parseISO(selectedDay), 'EEEE, MMMM d')} items={dayItems(selectedDay)} onItem={openItem} onAdd={() => openCreateForDate(selectedDay)} />
        </>
      ) : view === 'week' ? (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="cal-week">
          {weekDays.map((day) => {
            const ds = iso(day); const its = dayItems(ds);
            return (
              <Card key={ds} className="bg-[#111111] border-gray-800 p-2 min-h-[120px]">
                <div className="flex items-center justify-between mb-2">
                  <div className={`text-sm font-bold ${isToday(day) ? 'text-[#D4AF37]' : 'text-gray-300'}`}>{format(day, 'EEE d')}</div>
                  <button onClick={() => openCreateForDate(ds)} className="text-gray-500 hover:text-[#D4AF37]" aria-label="Add event"><Plus size={14} /></button>
                </div>
                <div className="space-y-1">
                  {its.length === 0 ? <p className="text-[11px] text-gray-600">—</p> : its.map((it) => <ItemPill key={it.calendar_item_id} it={it} onClick={() => openItem(it)} />)}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <AgendaCard title={format(current, 'EEEE, MMMM d, yyyy')} items={dayItems(iso(current))} onItem={openItem} onAdd={() => openCreateForDate(iso(current))} testId="cal-day" big />
      )}

      {/* Legend */}
      <Card className="bg-[#111111] border-gray-800 p-5 mt-6" data-testid="cal-legend">
        <h3 className="text-sm font-semibold text-white mb-3">Legend</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-2 uppercase">Manual Events</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {LEGEND_MANUAL.map((t) => <LegendDot key={t} t={t} />)}
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-2 uppercase">Automatic</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {LEGEND_AUTO.map((t) => <LegendDot key={t} t={t} />)}
              <span className="flex items-center gap-2 text-sm text-gray-500"><span className="h-2.5 w-2.5 rounded-full bg-gray-700" /> Employee / Crew Schedule <span className="text-[10px] text-gray-600">(Coming later)</span></span>
            </div>
          </div>
        </div>
      </Card>

      {/* Day "more" modal */}
      <Dialog open={!!dayModal} onOpenChange={(o) => !o && setDayModal(null)}>
        <DialogContent className="bg-[#111111] border-gray-800 text-white max-w-md" data-testid="cal-day-modal">
          <DialogHeader>
            <DialogTitle>{dayModal && format(parseISO(dayModal), 'EEEE, MMMM d')}</DialogTitle>
            <DialogDescription className="text-gray-500">Full schedule for this day</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {dayModal && dayItems(dayModal).map((it) => <AgendaRow key={it.calendar_item_id} it={it} onClick={() => { setDayModal(null); openItem(it); }} />)}
          </div>
        </DialogContent>
      </Dialog>

      {/* Read-only project detail */}
      <ItemDetailDialog item={detailItem} onClose={() => setDetailItem(null)} navigate={navigate} />

      {/* Create / Edit manual event */}
      <EventFormDialog open={createOpen} onOpenChange={setCreateOpen} mode="create" defaultDate={createDate} defaultType={createType}
        onSaved={(ev) => { if (ev?.start_date) { setCurrent(parseISO(ev.start_date)); setSelectedDay(ev.start_date); } load(); }} />
      <EventFormDialog open={editOpen} onOpenChange={setEditOpen} mode="edit" event={editEvent} onSaved={() => load()} />
    </div>
  );
};

const LegendDot = ({ t }) => (
  <span className="flex items-center gap-2 text-sm text-gray-400"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ITEM_COLORS[t] }} /> {ITEM_TYPE_LABEL[t]}</span>
);

const AgendaRow = ({ it, onClick }) => (
  <button onClick={onClick} className="w-full flex items-center gap-3 bg-[#161616] hover:bg-[#1C1C1C] border border-gray-800 rounded-lg px-3 py-2 text-left" data-testid={`cal-agenda-${it.calendar_item_id}`}>
    <span className="h-8 w-1 rounded-full shrink-0" style={{ backgroundColor: ITEM_COLORS[it.item_type] }} />
    <div className="min-w-0 flex-1">
      <div className="text-sm text-white font-medium truncate">{it.title}</div>
      <div className="text-xs text-gray-500">
        {it.is_all_day ? 'All day' : `${fmtTime(it.start_time)}${it.end_time ? ` – ${fmtTime(it.end_time)}` : ''}`} · {ITEM_TYPE_LABEL[it.item_type]}{it.project ? ` · ${it.project.name}` : ''}
      </div>
    </div>
  </button>
);

const AgendaCard = ({ title, items, onItem, onAdd, testId, big }) => (
  <Card className={`bg-[#111111] border-gray-800 p-5 ${big ? 'min-h-[300px]' : ''}`} data-testid={testId || 'cal-agenda'}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      <Button size="sm" variant="outline" onClick={onAdd} className="border-gray-700 text-gray-300"><Plus size={14} /> Add Event</Button>
    </div>
    {items.length === 0 ? (
      <div className="text-center py-10 text-gray-500"><CalendarX size={30} className="mx-auto mb-2 text-gray-700" />No scheduled items for this day.</div>
    ) : (
      <div className="space-y-2">{items.map((it) => <AgendaRow key={it.calendar_item_id} it={it} onClick={() => onItem(it)} />)}</div>
    )}
  </Card>
);

const ItemDetailDialog = ({ item, onClose, navigate }) => {
  if (!item) return null;
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111111] border-gray-800 text-white max-w-md" data-testid="cal-item-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: ITEM_COLORS[item.item_type] }} />
            {item.title}
          </DialogTitle>
          <DialogDescription className="text-gray-500">{ITEM_TYPE_LABEL[item.item_type]} · read-only</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm text-gray-300">
          <div><span className="text-gray-500">Date:</span> {item.start_date}</div>
          {item.status && <div><span className="text-gray-500">Status:</span> {eventStatusLabel(item.status)}</div>}
          {item.priority && <div><span className="text-gray-500">Priority:</span> {item.priority}</div>}
          <div><span className="text-gray-500">Project:</span> {item.project ? item.project.name : 'General'}</div>
        </div>
        <div className="flex justify-end pt-3">
          <Button onClick={() => { onClose(); navigate(item.source_route); }} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="cal-item-open">
            <ExternalLink size={16} /> Open Project
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
